import React, { FC, useEffect, useState } from 'react';
import { getConfigurationBySenior, updateVoicemailFlags } from '../../lib/supabase';

interface Props {
  isOpen: boolean;
  seniorId: string;
  seniorName: string;
  caregiverPhone?: string | null;
  caregiverSmsOptedOut?: boolean;
  onClose: () => void;
}

/**
 * F04 brief — per-senior caregiver toggle for the voicemail-waiting SMS.
 *
 *   Receive SMS twice daily if voicemail is waiting (11:00 + 15:00 local)
 *   Default OFF. Requires caregivers.phone present and
 *   caregivers.sms_opted_out=false (the CASL opt-in). Both gates surfaced
 *   inline so the caregiver understands why an enabled toggle may not fire.
 *
 * The indicator pill in the dashboard header is read-only (just shows the
 * current pending state); this drawer is the only place the SMS layer is
 * configured.
 */
export const VoicemailDrawer: FC<Props> = ({
  isOpen, seniorId, seniorName, caregiverPhone, caregiverSmsOptedOut, onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voicemailSmsEnabled, setVoicemailSmsEnabled] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const smsGateBlocked = !caregiverPhone || caregiverSmsOptedOut === true;

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getConfigurationBySenior(seniorId).then((cfg) => {
      if (cancelled) return;
      setVoicemailSmsEnabled(cfg?.voicemail_sms_enabled ?? false);
      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      console.error('[VoicemailDrawer] load failed', e);
      setError('Could not load voicemail SMS setting.');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [isOpen, seniorId]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const toggle = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await updateVoicemailFlags(seniorId, { voicemail_sms_enabled: next });
      setVoicemailSmsEnabled(next);
    } catch (e) {
      console.error('[VoicemailDrawer] update failed', e);
      setError('Could not save change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[1090] bg-brand-text/40 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 bottom-0 z-[1100] w-full sm:w-[420px] bg-white shadow-2xl
                   flex flex-col animate-slideInRight border-l border-brand-border"
        role="dialog"
        aria-labelledby="voicemail-drawer-title"
      >
        <div className="flex items-center justify-between p-5 border-b border-brand-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-amber-600" style={{ fontSize: '22px' }}>
                voicemail
              </span>
            </div>
            <div>
              <h2 id="voicemail-drawer-title" className="font-display text-lg font-bold text-brand-text leading-tight">
                Voicemail alerts
              </h2>
              <p className="text-xs text-brand-text-muted mt-0.5">For {seniorName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-brand-bg-warm transition-colors"
            aria-label="Close"
          >
            <span className="material-symbols-outlined text-brand-text-muted">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && (
            <div className="text-center text-sm text-brand-text-muted py-8">Loading…</div>
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          {!loading && (
            <>
              <section className="rounded-2xl border border-brand-border p-4 bg-white">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-brand-text leading-tight">
                      Receive SMS twice daily if voicemail is waiting
                    </h3>
                    <p className="text-xs text-brand-text-muted mt-1">
                      One reminder at 11:00 and one at 15:00 in your local time, only on days when
                      a voicemail is sitting unread on {seniorName}'s phone. Never sends after STOP.
                    </p>
                    {smsGateBlocked && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg mt-2 px-2 py-1.5">
                        {!caregiverPhone
                          ? <>Add your mobile number in <strong>My account</strong> first.</>
                          : <>You replied STOP to AidFone messages. Text START to <strong>+1 (450) 367-9692</strong> to re-opt in.</>}
                      </p>
                    )}
                  </div>
                  <ToggleButton
                    enabled={voicemailSmsEnabled}
                    onToggle={(v) => toggle(v)}
                    busy={saving}
                    disabled={smsGateBlocked}
                  />
                </div>
              </section>

              <p className="text-xs text-brand-text-muted leading-relaxed">
                You can prompt {seniorName} to listen — AidFone never reads the voicemail itself.
                Audio and caller identity stay on the senior's phone (Criminal Code s. 184 + Loi 25).
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

interface ToggleButtonProps {
  enabled: boolean;
  onToggle: (next: boolean) => void;
  busy?: boolean;
  disabled?: boolean;
}

const ToggleButton: FC<ToggleButtonProps> = ({ enabled, onToggle, busy, disabled }) => {
  const handleClick = () => { if (!busy && !disabled) onToggle(!enabled); };
  const bg = disabled ? 'bg-gray-200' : enabled ? 'bg-emerald-500' : 'bg-gray-300';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-busy={busy}
      onClick={handleClick}
      disabled={busy || disabled}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 ${bg}
                  ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                  ${busy ? 'animate-pulse' : ''}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200
                    ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  );
};

export default VoicemailDrawer;
